/**
 * Assemble a FargoRate results report from a completed tournament/instance.
 *
 * FargoRate has no public API — results are reported by emailing a results
 * spreadsheet to support@fargorate.com. This builds the rows (one per finished,
 * non-BYE singles match) and a CSV in a clear, Fargo-friendly layout.
 *
 * Players with no linked Fargo membership id are INCLUDED as "new" players (name +
 * contact where available) so FargoRate can establish a rating from the results —
 * that's how players first get into Fargo. Nothing is dropped except BYEs and
 * unfinished/placeholder matches.
 *
 * Report RAW games won (score1/score2), never handicap-adjusted: Fargo accepts
 * scratch and game-spot/race handicaps but not ball-spot.
 */
import { readBracketAsArray } from './bracketSchema';
import { buildPlayerUserIdMap } from './tournamentManager';
import { getUserProfile } from '../firebase/auth';

const isRealName = (n) =>
  !!n && n !== 'BYE' && !String(n).startsWith('Winner of') && !String(n).startsWith('Loser of');

/**
 * @param {object} source - the instance object (recurring) or the tournament (one-off)
 * @param {object} tournament - the root tournament (for fallback fields)
 * @param {object} opts - { eventName, date, gameType, winnersRace, losersRace, bracketUrl }
 * @returns {Promise<{ rows, newPlayers, meta, matchCount }>}
 */
export async function buildFargoReport(source, tournament, opts = {}) {
  const bracket = readBracketAsArray(source);
  const nameToUid = buildPlayerUserIdMap(source.playerUserIds || tournament.playerUserIds);

  const matches = bracket.filter(
    (m) => m && m.finished && m.winner &&
      isRealName(m.p1 && m.p1.name) && isRealName(m.p2 && m.p2.name) &&
      m.score1 != null && m.score2 != null
  );

  // Resolve each participant's Fargo id + contact (one profile fetch per uid).
  const names = new Set();
  matches.forEach((m) => { names.add(m.p1.name); names.add(m.p2.name); });

  const profileCache = new Map();
  const info = new Map(); // name -> { fargoId, isNew, registered, email, phone }
  for (const name of names) {
    const entry = nameToUid.get(name);
    let fargoId = null;
    let email = '';
    let phone = '';
    if (entry && entry.odUserId) {
      if (!profileCache.has(entry.odUserId)) {
        const { profile } = await getUserProfile(entry.odUserId);
        profileCache.set(entry.odUserId, profile || null);
      }
      const profile = profileCache.get(entry.odUserId);
      if (profile) {
        fargoId = (profile.ratings && profile.ratings.fargo && profile.ratings.fargo.membershipId) || null;
        email = profile.email || '';
        phone = profile.phone ? `${profile.phoneCountryCode || ''}${profile.phone}` : '';
      }
    }
    info.set(name, { fargoId, isNew: !fargoId, registered: !!(entry && entry.odUserId), email, phone });
  }

  const meta = {
    event: opts.eventName || tournament.title || 'Tournament',
    date: opts.date || '',
    gameType: opts.gameType || tournament.gameType || '',
    bracketUrl: opts.bracketUrl || '',
  };

  const raceFor = (m) =>
    m.bracket === 'L'
      ? (opts.losersRace || opts.winnersRace || '')
      : (opts.winnersRace || opts.losersRace || '');

  const rows = matches.map((m) => {
    const p1 = info.get(m.p1.name);
    const p2 = info.get(m.p2.name);
    return {
      date: meta.date,
      gameType: meta.gameType,
      race: raceFor(m),
      event: meta.event,
      p1Name: m.p1.name, p1FargoId: p1.fargoId || '', p1New: p1.isNew, p1Email: p1.email, p1Phone: p1.phone, p1Games: Number(m.score1) || 0,
      p2Name: m.p2.name, p2FargoId: p2.fargoId || '', p2New: p2.isNew, p2Email: p2.email, p2Phone: p2.phone, p2Games: Number(m.score2) || 0,
    };
  });

  const newPlayers = [...names]
    .filter((n) => info.get(n).isNew)
    .map((n) => ({ name: n, ...info.get(n) }));

  return { rows, newPlayers, meta, matchCount: rows.length };
}

// A player with no Fargo ID is implicitly "new" — Fargo establishes them from the
// results — so we drop the explicit NEW column, plus phone (email is enough contact).
const HEADERS = [
  'Event Name', 'Date', 'Race',
  'Player 1 Name', 'P1 Fargo ID', 'P1 Email',
  'Player 2 Name', 'P2 Fargo ID', 'P2 Email',
  'P1 Games Won', 'P2 Games Won',
];

const esc = (v) => {
  const s = String(v == null ? '' : v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function toFargoCsv(rows) {
  const lines = [HEADERS.join(',')];
  for (const r of rows) {
    lines.push([
      r.event, r.date, r.race,
      r.p1Name, r.p1FargoId, r.p1Email,
      r.p2Name, r.p2FargoId, r.p2Email,
      r.p1Games, r.p2Games,
    ].map(esc).join(','));
  }
  return lines.join('\n');
}
